<template>
  <div class="customer-group-list-container customer-group-list-main customer-group-list-page--glass glass-tab-page note-tag-list-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Note Tag List']" />

    <div class="glass-tab-view glass-tab-view--flush customer-group-list-glass-shell">
      <div class="glass-tab-view__content customer-group-list-tab-content customer-group-list-page-stack">
        <div class="customer-group-list-header-block">
          <div class="customer-group-list-header-row">
            <div class="customer-group-list-header-row__text">
              <h2>Note Tags</h2>
              <p class="customer-group-list-header-row__sub">
                Create tags for customer, vendor, and consultant notes
              </p>
            </div>
            <div class="customer-group-list-header-actions">
              <PrimaryButton label="Add New" @click="openAddModal" />
            </div>
          </div>

          <div class="note-tag-list-filter-row">
            <LabelDropdown
              label="Related To"
              select-id="note-tag-list-filter"
              v-model="filterRelatedTo"
              inline
              :options="relatedToOptions"
              @update:model-value="fetchTags"
            />
          </div>
        </div>

        <div class="customer-group-list-glass-panel customer-group-list-main-card">
          <div class="consultant-glass-table-section customer-group-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table customer-group-list-table">
                  <thead>
                    <tr>
                      <th class="note-tag-list-col note-tag-list-col--name">Name</th>
                      <th class="note-tag-list-col note-tag-list-col--color">Color</th>
                      <th class="note-tag-list-col note-tag-list-col--related">Related To</th>
                      <th class="note-tag-list-col note-tag-list-col--actions customer-group-list-actions-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="4" class="consultant-glass-table-no-records customer-group-list-no-records">
                        Loading...
                      </td>
                    </tr>
                    <tr v-else-if="listError">
                      <td colspan="4" class="consultant-glass-table-no-records customer-group-list-no-records">
                        {{ listError }}
                      </td>
                    </tr>
                    <tr v-else-if="tags.length === 0">
                      <td colspan="4" class="consultant-glass-table-no-records customer-group-list-no-records">
                        No records to show
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(tag, idx) in tags"
                        :key="tag.id || idx"
                        class="consultant-glass-table-row customer-group-list-row"
                        :style="{ '--cgl-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td class="note-tag-list-col note-tag-list-col--name">
                          <span class="note-tag-list-name-cell">
                            <span
                              class="note-tag-list-color-strip"
                              :style="{ backgroundColor: tag.color || '#94a3b8' }"
                              aria-hidden="true"
                            ></span>
                            {{ tag.name }}
                          </span>
                        </td>
                        <td class="note-tag-list-col note-tag-list-col--color">
                          <span
                            class="note-tag-list-color-preview"
                            :style="{ backgroundColor: tag.color || '#94a3b8' }"
                            :title="tag.color || 'No color'"
                          ></span>
                        </td>
                        <td class="note-tag-list-col note-tag-list-col--related">
                          <span
                            class="note-tag-list-related-badge"
                            :class="`note-tag-list-related-badge--${tag.relatedTo}`"
                          >
                            {{ formatRelatedTo(tag.relatedTo) }}
                          </span>
                        </td>
                        <td class="note-tag-list-col note-tag-list-col--actions customer-group-list-actions-cell">
                          <div class="customer-group-list-action-buttons" role="group" aria-label="Note tag actions">
                            <SecondaryButton
                              label=""
                              icon="bi bi-pencil"
                              custom-class="customer-group-list-action-btn customer-group-list-action-btn--edit"
                              aria-label="Edit"
                              title="Edit"
                              @click="openEditModal(tag)"
                            />
                            <SecondaryButton
                              label=""
                              icon="bi bi-trash"
                              custom-class="customer-group-list-action-btn customer-group-list-action-btn--delete"
                              aria-label="Delete"
                              title="Delete"
                              @click="openDeleteModal(idx)"
                            />
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddNewNoteTag
      :visible="showModal"
      :company-id="companyId"
      :default-related-to="filterRelatedTo"
      :editing-item="editingItem"
      @ok="handleTagSaved"
      @cancel="closeModal"
    />

    <SuccessModal
      :show="showSuccessModal"
      title="Success"
      :message="successMessage"
      buttonText="OK"
      @close="closeSuccessModal"
    />

    <ErrorModal
      :show="showErrorModal"
      title="Error"
      :message="errorMessage"
      buttonText="OK"
      @close="closeErrorModal"
    />

    <WarningModal
      :show="showWarningModal"
      title="Delete Note Tag"
      :message="warningMessage"
      buttonText="Cancel"
      confirmButtonText="Delete"
      @close="closeWarningModal"
      @confirm="confirmDeleteTag"
    />
  </div>
</template>

<script>
import '../styles/CustomerGroupList.css';
import '../styles/NoteTagList.css';
import AddNewNoteTag from './AddNewNoteTag.vue';
import noteTagService from '@/services/noteTag.service.js';
import logger from '@/utils/logger';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import WarningModal from '@/components/WarningModal.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';

const RELATED_TO_LABELS = {
  customer: 'Customer',
  vendor: 'Vendor',
  consultant: 'Consultant',
};

export default {
  name: 'NoteTagList',
  components: {
    AddNewNoteTag,
    BrucdcumHeader,
    SuccessModal,
    ErrorModal,
    WarningModal,
    PrimaryButton,
    SecondaryButton,
    LabelDropdown,
  },
  data() {
    return {
      showModal: false,
      editingItem: null,
      tags: [],
      loading: false,
      listError: '',
      companyId: this.$route.params.id,
      filterRelatedTo: 'customer',
      relatedToOptions: [
        { value: 'customer', label: 'Customer' },
        { value: 'vendor', label: 'Vendor' },
        { value: 'consultant', label: 'Consultant' },
      ],
      showSuccessModal: false,
      showErrorModal: false,
      showWarningModal: false,
      errorMessage: '',
      warningMessage: '',
      successMessage: '',
      deleteIdx: null,
      isDeleting: false,
    };
  },
  async mounted() {
    await this.fetchTags();
  },
  methods: {
    formatRelatedTo(value) {
      return RELATED_TO_LABELS[value] || value || '-';
    },
    openAddModal() {
      this.editingItem = null;
      this.showModal = true;
    },
    openEditModal(tag) {
      if (!tag?.id) return;
      this.editingItem = { ...tag };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingItem = null;
    },
    async fetchTags() {
      this.loading = true;
      this.listError = '';
      try {
        const response = await noteTagService.getNoteTags(this.companyId, this.filterRelatedTo);
        this.tags = response?.data?.data || [];
      } catch (err) {
        logger.error('Error fetching note tags:', err);
        this.listError =
          err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to load note tags.';
      } finally {
        this.loading = false;
      }
    },
    async handleTagSaved(savedTag) {
      const wasEdit = !!this.editingItem?.id;
      this.closeModal();
      if (savedTag?.relatedTo && savedTag.relatedTo !== this.filterRelatedTo) {
        this.filterRelatedTo = savedTag.relatedTo;
      }
      await this.fetchTags();
      const label = this.formatRelatedTo(savedTag?.relatedTo);
      this.successMessage = wasEdit
        ? `Note tag "${savedTag?.name || 'Item'}" (${label}) updated successfully.`
        : `Note tag "${savedTag?.name || 'New tag'}" (${label}) created successfully.`;
      this.showSuccessModal = true;
    },
    openDeleteModal(idx) {
      const tag = this.tags[idx];
      if (!tag?.id) {
        this.errorMessage = 'Invalid tag selected. Please refresh and try again.';
        this.showErrorModal = true;
        return;
      }
      this.deleteIdx = idx;
      this.warningMessage = `Delete note tag "${tag.name}" (${this.formatRelatedTo(tag.relatedTo)})? This cannot be undone.`;
      this.showWarningModal = true;
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.deleteIdx = null;
      this.warningMessage = '';
    },
    async confirmDeleteTag() {
      if (this.deleteIdx === null) return;

      const tag = this.tags[this.deleteIdx];
      if (!tag?.id) return;

      this.isDeleting = true;
      this.showWarningModal = false;

      try {
        await noteTagService.deleteNoteTag(tag.id, this.companyId);
        this.tags.splice(this.deleteIdx, 1);
        this.successMessage = `Note tag "${tag.name}" deleted successfully.`;
        this.showSuccessModal = true;
      } catch (err) {
        this.errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to delete note tag.';
        this.showErrorModal = true;
      } finally {
        this.isDeleting = false;
        this.deleteIdx = null;
        this.warningMessage = '';
      }
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
  },
};
</script>
